/**
 * Listener effects for the Auth WS channel.
 *
 * The auth-api pushes lightweight `Invalidate` frames when auth data changes
 * (users, roles, tenants, tenant memberships). These effects:
 * 1. authenticate the socket on open with the current access token, and
 * 2. map `Invalidate` frames to RTK Query tag invalidations, so subscribed
 *    queries refetch over the authorized HTTP API (Cerbos stays in the HTTP
 *    path — no entity data crosses the socket).
 *
 * Mirrors ws-assistant-effects.ts — see it for the pulse/keepalive notes.
 */

import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import {
  actionAuthOnOpen,
  actionAuthOnMessage,
  wsAuthSend,
  parseInvalidateMessage,
} from '@skynet/data-ws-auth';
import type { InvalidateMessage } from '@skynet/data-ws-auth';
import { selectAccessToken } from '@skynet/data-auth-api';
import { itemsApi } from '@skynet/data-items';
import { usersApi } from '@skynet/data-users';
import { rolesApi } from '@skynet/data-roles';
import { tenantsApi } from '@skynet/data-tenants';
import { userTenantMembershipsApi } from '@skynet/data-user-tenant-memberships';
import { debounceInvalidate } from './ws-invalidation-debounce';
import type { MappedInvalidations } from './ws-invalidation-debounce';

/** List tag id used by the data packages' `provideListTags` helper. */
const LIST_TAG_ID = 'LIST';

/**
 * Maps an `Invalidate` frame to the RTK Query invalidation actions to
 * dispatch. Auth frames map to cache invalidations only — nothing here is
 * stateful, so everything is safe to replay on the debounce's trailing edge
 * (see {@link MappedInvalidations}). Unknown entities map to no actions
 * (forward-compatible with new backend events).
 */
export const invalidateAuthActionsFor = (msg: InvalidateMessage): MappedInvalidations => {
  const { entity, id } = msg;

  switch (entity) {
    case 'items':
      // publish/unpublish arrive as `updated` — same caches either way
      return {
        cache: [
          itemsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Items', id },
                  { type: 'Items', id: LIST_TAG_ID },
                ]
              : ['Items'],
          ),
        ],
      };

    case 'users':
      return {
        cache: [
          usersApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Users', id },
                  { type: 'Users', id: LIST_TAG_ID },
                ]
              : ['Users'],
          ),
        ],
      };

    case 'roles':
      return {
        cache: [
          rolesApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Roles', id },
                  { type: 'Roles', id: LIST_TAG_ID },
                ]
              : ['Roles'],
          ),
        ],
      };

    case 'tenants':
      // Tenant caches are keyed by idOrSlug; the event carries the numeric
      // id, so slug-keyed detail entries are only refreshed via the LIST tag
      return {
        cache: [
          tenantsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Tenants', id },
                  { type: 'Tenants', id: LIST_TAG_ID },
                ]
              : ['Tenants'],
          ),
        ],
      };

    case 'tenant-memberships':
      // The event id is the affected userId (matches UserMemberships tag
      // ids); tenant-scoped member lists are invalidated type-wide
      return {
        cache: [
          userTenantMembershipsApi.util.invalidateTags(
            id !== undefined ? [{ type: 'UserMemberships', id }] : ['UserMemberships'],
          ),
          tenantsApi.util.invalidateTags(['TenantMembers']),
        ],
      };

    // Unknown/future entities: no-op (forward-compatible with new backend events)
    default:
      return { cache: [] };
  }
};

export const startWsAuthEffects = () => {
  // Authenticate the socket as soon as it opens (and on every reconnect) with
  // a fresh access token from the store — same source the HTTP clients use.
  //
  // Auth failure needs no handling here: the server replies with an error
  // frame and closes the socket with 4401, ws-core's reconnectOnClose then
  // reconnects, and this listener re-auths with whatever token is in the
  // store at that moment (fresh after a token refresh).
  const authOnOpenAbort = startAppListening({
    actionCreator: actionAuthOnOpen,
    effect: (_action, listenerApi) => {
      const token = selectAccessToken(listenerApi.getState());

      if (token) {
        listenerApi.dispatch(wsAuthSend({ command: 'Auth', token }));
      }
    },
  });

  const invalidateOnMessageAbort = startAppListening({
    actionCreator: actionAuthOnMessage,
    effect: (action, listenerApi) => {
      const msg = parseInvalidateMessage(action.payload);

      if (!msg) {
        return;
      }

      debounceInvalidate('auth', msg, invalidateAuthActionsFor(msg), listenerApi.dispatch);
    },
  });

  return () => {
    authOnOpenAbort();
    invalidateOnMessageAbort();
  };
};
